import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheet } from './BottomSheet';
import { useMe } from '../../hooks/useMe';
import { useTelegram } from '../../hooks/useTelegram';
import { hasApiAuth } from '../../lib/webSession';
import { ReauthFlow } from './ReauthFlow';
import { useHaptic } from '../../hooks/useHaptic';
import { useStakingActions } from '../../hooks/useStaking';
import {
  stakingErrorMessage,
  type StakePosition,
  type WithdrawDestination,
} from '../../lib/staking';
import { fmtUsdt, tint, type PositionMeta } from './stakeMeta';

type Props = {
  position: StakePosition | null;
  meta: PositionMeta | null;
  onClose: () => void;
  /** Report the outcome so the screen can raise a toast. */
  onDone: (message: string) => void;
  /** Flat USDT fees from the staking overview. */
  fees?: { walletUsd: number; externalUsd: number };
};

const WalletGlyph = ({ color }: { color: string }) => (
  <svg
    width="18"
    height="18"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 24 24"
  >
    <path d="M3 8.5A2.5 2.5 0 0 1 5.5 6h13A2.5 2.5 0 0 1 21 8.5v8a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 16.5Z" />
    <path d="M16.5 12.5h1.5" />
  </svg>
);

const ExternalGlyph = ({ color }: { color: string }) => (
  <svg
    width="18"
    height="18"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 24 24"
  >
    <path d="M12 19V5m-6 6 6-6 6 6" />
  </svg>
);

/** Fee comes out of the withdrawn body — same as server `quoteWithdrawal`. */
function receiveAfterFee(amount: number, fee: number) {
  return Math.max(0, amount - fee);
}

const MIN_USDT_PAYOUT = 0.000001;

/**
 * Where a withdrawal goes. Tapping a destination takes out everything the
 * position currently allows — the same one-tap flow as the prototype.
 * Card payout is disabled; wallet and external TRX remain.
 */
export function StakeWithdrawSheet({
  position,
  meta,
  onClose,
  onDone,
  fees = { walletUsd: 0, externalUsd: 1 },
}: Props) {
  const { t } = useTranslation();
  const { impact, notify } = useHaptic();
  const { me } = useMe();
  const { initData } = useTelegram();
  const { withdraw } = useStakingActions();
  const [address, setAddress] = useState('');
  const [askAddress, setAskAddress] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reauthOpen, setReauthOpen] = useState(false);
  /** Held across the reauth round trip so the retry sends the same request. */
  const pending = useRef<{ destination: WithdrawDestination; message: string } | null>(null);

  const open = !!position && !!meta;
  const available = position?.available ?? 0;
  const onlyProfit = meta?.withdrawMode === 'profit';
  const accent = meta?.accent ?? '#34c759';

  const walletReceive = receiveAfterFee(available, fees.walletUsd);
  const externalReceive = receiveAfterFee(available, fees.externalUsd);
  const walletOk = available >= MIN_USDT_PAYOUT && walletReceive >= MIN_USDT_PAYOUT;
  const externalOk = available >= MIN_USDT_PAYOUT && externalReceive >= MIN_USDT_PAYOUT;

  const needsReauth = !!me?.pinSet || !!me?.biometricEnabled;

  useEffect(() => {
    if (!open) return;
    setError(null);
    setAskAddress(false);
    setAddress('');
    pending.current = null;
  }, [open, position?.id]);

  const send = async (destination: WithdrawDestination, message: string, reauthToken?: string) => {
    if (!position || !meta) return;
    const fee = destination.type === 'external' ? fees.externalUsd : fees.walletUsd;
    if (receiveAfterFee(available, fee) < MIN_USDT_PAYOUT) {
      setError(t('stake.errBelowFee'));
      return;
    }
    impact('medium');
    setError(null);
    // Moving money out is reauth-gated server-side; ask before the request so
    // the user sees the prompt instead of a bare failure.
    if (needsReauth && !reauthToken) {
      pending.current = { destination, message };
      setReauthOpen(true);
      return;
    }
    try {
      const result = await withdraw.mutateAsync({
        position,
        amount: available,
        destination,
        mode: meta.withdrawMode,
        reauthToken,
      });
      notify('success');
      const quote = result?.quote;
      onDone(
        quote
          ? `${message} · ${t('stake.withdrawAmount')}: ${fmtUsdt(quote.amount)} · ${t(
              'stake.withdrawFee',
            )}: ${fmtUsdt(quote.fee)} · ${t('stake.withdrawReceive')}: ${fmtUsdt(quote.receive)}`
          : message,
      );
      pending.current = null;
      setAddress('');
      setAskAddress(false);
      setError(null);
      onClose();
    } catch (e) {
      notify('error');
      const msg = String(e);
      if (msg.includes('reauth_required')) {
        pending.current = { destination, message };
        setReauthOpen(true);
        return;
      }
      setError(stakingErrorMessage(msg, t, t('stake.errGeneric')));
    }
  };

  return (
    <BottomSheet open={open} onClose={onClose}>
      {position && meta && (
        <div className="pb-2">
          <div className="pt-1 text-center">
            <div className="text-[19px] font-semibold tracking-[-0.4px] text-ink">
              {onlyProfit ? t('stake.withdrawProfitTitle') : t('stake.withdrawTitle')}
            </div>
            <div className="mt-1 text-[13px] text-muted">
              {t('stake.withdrawSub', {
                vault: meta.name,
                id: position.id,
                amount: fmtUsdt(available),
              })}
            </div>
          </div>

          <div className="mt-3.5 flex flex-col gap-2">
            <DestinationRow
              tintColor={tint(accent, 0.12)}
              icon={<WalletGlyph color={accent} />}
              title={t('stake.toWallet')}
              hint={
                fees.walletUsd > 0
                  ? t('stake.toWalletHintFee', {
                      fee: fees.walletUsd,
                      receive: fmtUsdt(walletReceive),
                    })
                  : t('stake.toWalletHint')
              }
              disabled={withdraw.isPending || !walletOk}
              onClick={() => {
                if (!walletOk) {
                  setError(t('stake.errBelowFee'));
                  return;
                }
                void send({ type: 'wallet' }, t('stake.sentWallet'));
              }}
            />

            <DestinationRow
              tintColor={tint(accent, 0.12)}
              icon={<ExternalGlyph color={accent} />}
              title={t('stake.toExternal')}
              hint={t('stake.toExternalHint', {
                fee: fees.externalUsd,
                receive: fmtUsdt(externalReceive),
              })}
              disabled={withdraw.isPending || !externalOk}
              onClick={() => {
                if (!externalOk) {
                  setError(t('stake.errBelowFee'));
                  return;
                }
                setError(null);
                setAskAddress((v) => !v);
              }}
            />

            {askAddress && (
              <div className="rounded-[16px] bg-black/[0.04] p-3">
                <div className="mb-2.5 flex flex-col gap-1 text-[12.5px]">
                  <div className="flex justify-between text-muted">
                    <span>{t('stake.withdrawAmount')}</span>
                    <span className="font-medium text-ink">{fmtUsdt(available)}</span>
                  </div>
                  <div className="flex justify-between text-muted">
                    <span>{t('stake.withdrawFee')}</span>
                    <span className="font-medium text-ink">{fmtUsdt(fees.externalUsd)}</span>
                  </div>
                  <div className="flex justify-between text-muted">
                    <span>{t('stake.withdrawReceive')}</span>
                    <span className="font-semibold text-ink">{fmtUsdt(externalReceive)}</span>
                  </div>
                </div>
                <input
                  value={address}
                  onChange={(e) => setAddress(e.target.value.trim())}
                  placeholder={t('stake.externalPlaceholder')}
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-label={t('stake.toExternal')}
                  className="w-full bg-transparent font-mono text-[13px] text-ink outline-none placeholder:text-black/25"
                />
                <button
                  type="button"
                  disabled={address.length < 30 || withdraw.isPending || !externalOk}
                  onClick={() =>
                    void send({ type: 'external', chain: 'trx', address }, t('stake.sentExternal'))
                  }
                  className="mt-2.5 h-10 w-full rounded-[12px] text-[14px] font-semibold text-white disabled:opacity-40"
                  style={{ background: accent }}
                >
                  {t('stake.withdraw')}
                </button>
              </div>
            )}
          </div>

          {error && <p className="mt-2.5 text-[12px] font-semibold text-negative">{error}</p>}

          <p className="mt-3 text-[11.5px] leading-[1.5] text-muted">
            {onlyProfit ? t('stake.noteProfit') : t('stake.noteFull', { vault: meta.name })}
          </p>
          {(fees.walletUsd > 0 || fees.externalUsd > 0) && (
            <p className="mt-1.5 text-[11.5px] leading-[1.5] text-muted">
              {t('stake.feeFromAmount')}
            </p>
          )}

          <button
            type="button"
            onClick={onClose}
            className="mt-3.5 h-12 w-full rounded-[15px] bg-black/[0.05] text-[15px] font-medium text-ink active:bg-black/[0.1]"
          >
            {t('common.cancel')}
          </button>
        </div>
      )}
      {reauthOpen && hasApiAuth(initData) && (
        <div className="push-enter fixed inset-0 z-[70] overflow-y-auto bg-holo">
          <ReauthFlow
            initData={initData}
            biometricEnabled={!!me?.biometricEnabled}
            onSuccess={(token) => {
              setReauthOpen(false);
              const p = pending.current;
              if (p) void send(p.destination, p.message, token);
            }}
            onCancel={() => {
              setReauthOpen(false);
              pending.current = null;
            }}
          />
        </div>
      )}
    </BottomSheet>
  );
}

function DestinationRow({
  tintColor,
  icon,
  title,
  hint,
  disabled,
  onClick,
}: {
  tintColor: string;
  icon: ReactNode;
  title: string;
  hint: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-[16px] bg-black/[0.03] px-3.5 py-3 text-left active:bg-black/[0.06] disabled:opacity-50"
    >
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
        style={{ background: tintColor }}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] font-medium text-ink">{title}</span>
        <span className="block truncate text-[12px] text-muted">{hint}</span>
      </span>
    </button>
  );
}
